import React, { useState, useEffect } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { config } from "@/config";
import { processPrivacyAddress } from "@/utils/addressUtils";

interface AddressValidationProps {
  form: any;
  addressValidated: boolean;
  setAddressValidated: (validated: boolean) => void;
  selectedLocation: any | null;
  setSelectedLocation: (location: any | null) => void;
  isEditing?: boolean;
  reviewId?: string;
}

export const AddressValidation: React.FC<AddressValidationProps> = ({
  form,
  addressValidated,
  setAddressValidated,
  selectedLocation,
  setSelectedLocation,
  isEditing = false,
  reviewId
}) => {
  const { language } = useLanguage();
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [fullAddressData, setFullAddressData] = useState<any>(null);

  useEffect(() => {
    if (isEditing && reviewId) {
      console.log("🔥 AddressValidation: Fetching address for editing mode");
      const fetchAddress = async () => {
        try {
          const { data, error } = await supabase
            .from('reviews')
            .select('address_detail, coordinates')
            .eq('id', reviewId)
            .single();

          console.log("🔥 AddressValidation: Fetched data:", { data, error });

          if (error) {
            console.error("Error fetching address:", error);
          }

          if (data && data.address_detail) {
            console.log("🔥 AddressValidation: Setting address and validation");
            setSearchTerm(data.address_detail);
            setAddressValidated(true);
            
            // 同時設定座標（如果有的話）
            if (data.coordinates) {
              setSelectedLocation(data.coordinates);
              console.log("🔥 AddressValidation: Set coordinates:", data.coordinates);
            }
          }
        } catch (error) {
          console.error("Error fetching address:", error);
        }
      };

      fetchAddress();
    }
  }, [isEditing, reviewId]);

  const handleAddressChange = async (value: string) => {
    setSearchTerm(value);
    setAddressValidated(false);
    setSelectedLocation(null);
    setFullAddressData(null);
    setLoading(true);
    setSuggestions([]);

    if (value.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    try {
      // 檢查 Google Maps API 是否已載入
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log("🔥 Google Maps API not loaded yet, skipping autocomplete");
        setLoading(false);
        
        // 編輯模式下，如果已有地址，直接設為驗證成功
        if (isEditing && value.length > 10) {
          console.log("🔥 Editing mode: Auto-validating existing address");
          setAddressValidated(true);
          // 設定一個基本座標 (Calgary 中心)
          if (!selectedLocation) {
            setSelectedLocation({ lat: 51.0447, lng: -114.0719 });
          }
        }
        return;
      }

      const service = new google.maps.places.AutocompleteService();
      
      service.getPlacePredictions(
        {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'ca' }
        },
        (predictions, status) => {
          setLoading(false);
          console.log("🔥 Google Places API response:", { status, predictions: predictions?.length });
          
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.slice(0, 5));
          } else {
            setSuggestions([]);
            
            // 編輯模式下的容錯處理
            if (isEditing && value.length > 10) {
              console.log("🔥 API failed but editing mode: Auto-validating address");
              setAddressValidated(true);
              if (!selectedLocation) {
                setSelectedLocation({ lat: 51.0447, lng: -114.0719 });
              }
            }
          }
        }
      );
    } catch (error) {
      console.error("🔥 Error fetching address suggestions:", error);
      setSuggestions([]);
      setLoading(false);
      
      // 編輯模式下的容錯處理
      if (isEditing && value.length > 10) {
        console.log("🔥 Exception occurred but editing mode: Auto-validating address");
        setAddressValidated(true);
        if (!selectedLocation) {
          setSelectedLocation({ lat: 51.0447, lng: -114.0719 });
        }
      }
    }
  };

  const handleSuggestionClick = async (suggestion: any) => {
    const geocoder = new google.maps.Geocoder();
    
    geocoder.geocode({ placeId: suggestion.place_id }, async (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const result = results[0];
        const fullAddress = result.formatted_address;
        const fullCoordinates = {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng()
        };

        // 儲存完整地址資料
        const addressData = {
          fullAddress,
          fullCoordinates,
          addressComponents: result.address_components
        };
        setFullAddressData(addressData);

        // 設定表單值為完整地址
        form.setValue("address_detail", fullAddress);
        setSearchTerm(fullAddress);
        setAddressValidated(true);
        setSelectedLocation(fullCoordinates);
        
        // 同時處理隱私地址（如果需要）
        const hideStreetNumber = form.getValues("hide_street_number");
        if (hideStreetNumber) {
          const privacyResult = await processPrivacyAddress(
            fullAddress,
            fullCoordinates,
            hideStreetNumber,
            result.address_components
          );
          
          // 儲存處理後的地址資料
          form.setValue("display_address", privacyResult.displayAddress);
          form.setValue("display_coordinates", privacyResult.displayCoordinates);
        }

        setSuggestions([]);
        setInputFocused(false);
      } else {
        console.error('Geocoding failed:', status);
      }
    });
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (!inputFocused) {
        setSuggestions([]);
      }
    }, 200);
  };

  // Load Google Maps API if not already loaded
  useEffect(() => {
    // 檢查是否已有 Google Maps API script
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    
    if (!window.google && !existingScript) {
      console.log("🔥 Loading Google Maps API for the first time");
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("✅ Google Maps API loaded successfully");
      };
      script.onerror = () => {
        console.error("🔥 Failed to load Google Maps API");
        // 編輯模式下，即使Google Maps API載入失敗也允許提交
        if (isEditing) {
          console.log("🔥 Editing mode: Setting fallback validation");
          setAddressValidated(true);
          if (!selectedLocation) {
            setSelectedLocation({ lat: 51.0447, lng: -114.0719 });
          }
        }
      };
      document.head.appendChild(script);
    } else if (window.google) {
      console.log("✅ Google Maps API already available");
    } else {
      console.log("🔥 Google Maps API script already exists, waiting for load");
    }
  }, [isEditing]);

  // 編輯模式下設置地址驗證為已通過
  useEffect(() => {
    if (isEditing) {
      setAddressValidated(true);
    }
  }, [isEditing]);

  return (
    <FormField
      control={form.control}
      name="address_detail"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-lg font-semibold">
            {language === 'en' ? 'Address' : '地址'}
            <span className="text-red-500 ml-1">*</span>
            {isEditing && (
              <span className="text-sm text-muted-foreground font-normal ml-2">
                {language === 'en' ? '(Cannot be changed during editing)' : '（編輯時不可更改）'}
              </span>
            )}
          </FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                placeholder={language === 'en' ? "Enter complete address including house number" : "請輸入完整地址（包含門牌號碼）"}
                disabled={isEditing}
                readOnly={isEditing}
                onChange={(e) => {
                  if (!isEditing) {
                    field.onChange(e);
                    handleAddressChange(e.target.value);
                  }
                }}
                onFocus={() => !isEditing && setInputFocused(true)}
                onBlur={!isEditing ? handleInputBlur : undefined}
                className={`${addressValidated ? "border-green-500" : ""} ${isEditing ? "cursor-not-allowed opacity-75" : ""}`}
              />
              {!isEditing && loading && (
                <div className="absolute right-2 top-2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {!isEditing && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.place_id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                      onClick={() => handleSuggestionClick(suggestion)}
                      onMouseEnter={() => setInputFocused(true)}
                      onMouseLeave={() => setInputFocused(false)}
                    >
                      <div className="font-medium">{suggestion.description}</div>
                      {suggestion.structured_formatting && (
                        <div className="text-xs text-gray-500 mt-1">
                          {suggestion.structured_formatting.secondary_text}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FormControl>
          {addressValidated && (
            <div className="text-sm text-green-600">
              {language === 'en' ? '✓ Address validated' : '✓ 地址驗證成功'}
            </div>
          )}
          {!isEditing && !addressValidated && searchTerm.length >= 3 && suggestions.length === 0 && !loading && (
            <div className="text-sm text-orange-600">
              {language === 'en' 
                ? 'No addresses found. Please check your input or try a different address.' 
                : '找不到地址。請檢查您的輸入或嘗試其他地址。'}
            </div>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
